import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import BackMethods from "../../back_component/BackMethods";
import {
    FaClipboardList, FaSearch, FaPhoneAlt, FaStore, FaUserShield,
    FaCheckCircle, FaClock, FaChevronLeft, FaChevronRight, FaCalendarDay, FaExclamationCircle
} from "react-icons/fa";

export default function ComplaintsDashboard() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { backEnd_request } = BackMethods();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [complaints, setComplaints] = useState([]);
    const [totalCount, setTotalCount] = useState(0);

    const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
    const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "unprocessed");
    const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page") || 1));
    const [pageSize, setPageSize] = useState(parseInt(searchParams.get("pageSize") || 10));

    // زيادة عدد الكلمات المعروضة
    const truncateWords = (text, limit) => {
        if (!text) return "---";
        const words = text.split(" ");
        return words.length > limit ? words.slice(0, limit).join(" ") + "..." : text;
    };

    const fetchComplaints = useCallback(async () => {
        setLoading(true);
        try {
            const endpoint = activeTab === "processed"
                ? "/public/staff/complaints/processed"
                : "/public/staff/complaints/unprocessed";

            const response = await backEnd_request.get(endpoint, {
                params: { page: currentPage, pageSize: pageSize, take: pageSize, search: searchTerm }
            });
            console.log(response);
            if (response.data) {
                setComplaints(response.data.complaints
                    || response.data || []);
                setTotalCount(response.data.totalCount || response.data.length || 0);
            }
        } catch (err) {
            console.error("Error:", err);
            setComplaints([]);
        } finally {
            setLoading(false);
        }
    }, [activeTab, currentPage, pageSize, searchTerm, backEnd_request]);

    useEffect(() => {
        fetchComplaints();
        setSearchParams({ tab: activeTab, page: currentPage, pageSize, search: searchTerm });
    }, [fetchComplaints]);

    const handleTabChange = (tab) => { setActiveTab(tab); setCurrentPage(1); };
    const handleSearchChange = (val) => { setSearchTerm(val); setCurrentPage(1); };

    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <div className="complaints-wrapper bg-light min-vh-100 py-3 py-md-4" dir="rtl">
            <div className="container-fluid px-1 px-md-3"> {/* توسيع مجال العرض الجانبي */}

                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-3 mb-md-4 bg-white p-3 rounded-4 shadow-sm border-bottom border-primary border-3">
                    <div className="d-flex align-items-center gap-2">
                        <div className="bg-primary text-white p-2 rounded-3 d-sm-flex d-none">
                            <FaClipboardList size={20} />
                        </div>
                        <div>
                            <h1 className="fw-bold text-dark m-0 fs-5">سجل الشكاوي</h1>
                            <span className="badge bg-primary-subtle text-primary rounded-pill px-2 small">إجمالي: {totalCount}</span>
                        </div>
                    </div>
                    <button onClick={() => navigate(-1)} className="btn btn-outline-dark rounded-pill px-3 btn-sm fw-bold">رجوع</button>
                </div>

                {/* Search & Tabs */}
                <div className="row g-2 g-md-3 mb-4">
                    <div className="col-12 col-lg-7 order-2 order-lg-1">
                        <div className="search-input-wrapper bg-white shadow-sm rounded-pill d-flex align-items-center px-3 border">
                            <FaSearch className="text-muted ms-2" />
                            <input
                                type="text"
                                className="form-control border-0 py-2 py-md-3 bg-transparent"
                                placeholder="ابحث برقم الهاتف، المنشأة..."
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="col-12 col-lg-5 order-1 order-lg-2">
                        <div className="tab-pill-container bg-white p-1 rounded-pill shadow-sm d-flex border">
                            <button className={`tab-pill-btn flex-grow-1 ${activeTab === 'unprocessed' ? 'active-unprocessed' : ''}`} onClick={() => handleTabChange("unprocessed")}>
                                <FaClock className="ms-1" /> قيد الانتظار
                            </button>
                            <button className={`tab-pill-btn flex-grow-1 ${activeTab === 'processed' ? 'active-processed' : ''}`} onClick={() => handleTabChange("processed")}>
                                <FaCheckCircle className="ms-1" /> معالجة
                            </button>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5"><div className="spinner-grow text-primary"></div></div>
                ) : (
                    <>
                        {/* DESKTOP VIEW */}
                        <div className="table-responsive d-none d-lg-block">
                            <table className="table custom-modern-table mb-0">
                                <thead>
                                    <tr>
                                        <th className="text-center" style={{ width: '50px' }}>#</th>
                                        <th style={{ width: '200px' }}>المنشأة</th>
                                        <th>مضمون الشكوى</th>
                                        {activeTab === "processed" && <th style={{ width: '150px' }}>الموظف</th>}
                                        <th style={{ width: '180px' }}>التواريخ</th>
                                        <th className="text-center" style={{ width: '80px' }}>التفاصيل</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {complaints.map((c, index) => (
                                        <tr key={c.complaintUid} onClick={() => navigate(`/admin/complaint-details?uid=${c.complaintUid}`)} style={{ cursor: 'pointer' }}>
                                            <td className="text-center fw-bold text-muted">{(currentPage - 1) * pageSize + index + 1}</td>
                                            <td>
                                                <div className="fw-bold text-dark"><FaStore className="ms-1 text-primary" size={14} /> {c.establishment?.name}</div>
                                            </td>
                                            <td>
                                                {/* عرض النص على سطرين مع زيادة عدد الكلمات */}
                                                <p className="m-0 text-dark small line-clamp-2" title={c.complaintText}>
                                                    {truncateWords(c.complaintText, 35)}
                                                </p>
                                            </td>
                                            {activeTab === "processed" && (
                                                <td>
                                                    <div className="small fw-bold text-secondary">
                                                        <FaUserShield className="ms-1 text-muted" size={12} />
                                                        {c.processedByStaffName || "غير محدد"}
                                                    </div>
                                                </td>
                                            )}
                                            <td>
                                                <div className="small text-dark mb-1"><FaCalendarDay size={11} className="ms-1 text-info" /> {c.createdAtUtc?.split("T")[0]}</div>
                                                {activeTab === "processed" && (
                                                    <div className="small text-success fw-bold"><FaCheckCircle size={11} className="ms-1" /> {c.processedAtUtc?.split("T")[0]}</div>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <button className="btn btn-sm btn-light rounded-circle shadow-sm"><FaChevronLeft size={10} /></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* MOBILE VIEW */}
                        <div className="d-lg-none d-flex flex-column gap-3">
                            {complaints.map((c, index) => (
                                <div key={c.complaintUid} className="mobile-card" onClick={() => navigate(`/admin/complaint-details?uid=${c.complaintUid}`)}>
                                    <div className="card-top-section d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="card-index">#{(currentPage - 1) * pageSize + index + 1}</span>
                                            <div className="fw-bold text-dark"><FaStore className="ms-1 text-primary" /> {c.establishment?.name}</div>
                                        </div>
                                        <FaChevronLeft className="text-muted opacity-50" size={12} />
                                    </div>
                                    <div className="card-content-box my-3">
                                        <p className="m-0 text-dark-emphasis line-clamp-2">{truncateWords(c.complaintText, 40)}</p>
                                    </div>
                                    <div className="card-footer-info d-flex justify-content-between align-items-end pt-2">
                                        <div className="d-flex flex-column gap-1">
                                            <span className="phone-pill mb-1"><FaPhoneAlt className="ms-1" size={10} /> {c.submitterPhone}</span>
                                            <span className="date-text"><FaCalendarDay className="ms-1 text-info" /> {c.createdAtUtc?.split("T")[0]}</span>
                                            {activeTab === "processed" && (
                                                <>
                                                    <span className="date-text text-success fw-bold"><FaCheckCircle className="ms-1" /> {c.processedAtUtc?.split("T")[0]}</span>
                                                    <span className="date-text text-secondary small"><FaUserShield className="ms-1" /> {c.processedByStaffName}</span>
                                                </>
                                            )}
                                        </div>
                                        <span className={`mobile-status-pill ${activeTab === 'processed' ? 'success' : 'pending'}`}>
                                            {activeTab === 'processed' ? 'معالجة' : 'في الانتظار'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination Bar */}
                        {totalCount > 0 && (
                            <div className="pagination-container d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 bg-white p-3 rounded-4 shadow-sm border">
                                <div className="text-muted small mb-3 mb-md-0">
                                    عرض <strong>{complaints.length}</strong> من <strong>{totalCount}</strong>
                                </div>
                                <div className="d-flex align-items-center gap-3">
                                    <select className="form-select form-select-sm w-auto border-0 bg-light rounded-pill px-3" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
                                        <option value={10}>10 صفوف</option>
                                        <option value={20}>20 صفوف</option>
                                        <option value={50}>50 صفوف</option>
                                    </select>
                                    <div className="pagination-controls d-flex gap-2">
                                        <button className="btn btn-sm btn-light rounded-circle shadow-sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                                            <FaChevronRight size={12} />
                                        </button>
                                        <span className="fw-bold small align-self-center px-2">{currentPage} من {totalPages || 1}</span>
                                        <button className="btn btn-sm btn-light rounded-circle shadow-sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                                            <FaChevronLeft size={12} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <style>{`
                .complaints-wrapper { font-family: 'Cairo', sans-serif; }
                
                /* عرض النص على سطرين فقط مع وضع نقاط اختصار */
                .line-clamp-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;  
                    overflow: hidden;
                    line-height: 1.5;
                }

                .tab-pill-btn {
                    flex: 1; border: none; background: transparent; padding: 10px 5px; 
                    border-radius: 50px; color: #6c757d; font-weight: 700; font-size: 0.85rem;
                    transition: all 0.3s ease; display: flex; align-items: center; justify-content: center;
                }
                .active-unprocessed { background: #dc3545 !important; color: white !important; box-shadow: 0 4px 12px rgba(220, 53, 69, 0.2); }
                .active-processed { background: #198754 !important; color: white !important; box-shadow: 0 4px 12px rgba(25, 135, 84, 0.2); }
                
                .custom-modern-table { border-collapse: separate !important; border-spacing: 0 10px !important; width: 100%; }
                .custom-modern-table tbody tr { background: white; transition: 0.2s; }
                .custom-modern-table tbody tr:hover { transform: translateY(-2px); box-shadow: 0 4px 10px rgba(0,0,0,0.05); }
                .custom-modern-table td { padding: 12px 15px; vertical-align: middle; border: none; }
                .custom-modern-table td:first-child { border-top-right-radius: 15px; border-bottom-right-radius: 15px; }
                .custom-modern-table td:last-child { border-top-left-radius: 15px; border-bottom-left-radius: 15px; }

                .mobile-card {
                    background: white; padding: 16px; border-radius: 20px; border: 1px solid rgba(0,0,0,0.05); 
                    box-shadow: 0 4px 15px rgba(0,0,0,0.03); transition: 0.2s;
                }
                .card-content-box { background: #f9f9f9; padding: 12px; border-radius: 15px; font-size: 0.88rem; border-right: 3px solid #dee2e6; }
                .phone-pill { color: #0d6efd; font-weight: 800; font-size: 0.8rem; background: rgba(13, 110, 253, 0.08); padding: 3px 10px; border-radius: 50px; display: inline-block; }
                .date-text { color: #adb5bd; font-size: 0.75rem; font-weight: 600; display: block;}
                .mobile-status-pill { font-size: 0.75rem; font-weight: 800; padding: 6px 14px; border-radius: 12px; }
                .mobile-status-pill.pending { background: #fff4e5; color: #b95000; }
                .mobile-status-pill.success { background: #e6fcf5; color: #087f5b; }
            `}</style>
        </div>
    );
}   